// Cold-start-aware transport for hosted apps (PR1 — HTTP cold-start contract,
// §5.B.4). Hosted apps that do not have the platform shell's `sharedAuthFetch`
// use these wrappers so every plain `fetch` and every dynamic `import()` of a
// remote bundle is bounded-retry cold-start-aware in one place.
//
// `coldStartAwareFetch` mirrors the bounded cold-start branch of the shell's
// `createAuthFetch` (auth/token-manager.ts) but without the auth/refresh and
// maintenance overlay — those are shell-only concerns. A 503+Retry-After
// (maintenance/migration) classifies as `maintenance` and is returned as-is:
// hosted apps do not own the maintenance overlay.
import { callerSignalOf, classifyResponse, sleepUnlessAborted, startWait } from '../auth/token-manager-utils.js';
import { bustBundleUrl, coldStartBackoffMs, isColdStartRetryable, loadWithColdStartRetry, probeColdStart, } from './cold-start.js';
/** Promise-based sleep — the transport's default backoff timer. Injectable so
 * tests stay instant. */
export function defaultSleep(ms) {
    return new Promise((resolve) => { setTimeout(resolve, ms); });
}
/**
 * Native dynamic `import()` of a remote bundle specifier. Isolated as a named
 * function so it is both the default importer for {@link importRemoteBundle}
 * and directly unit-testable. `@vite-ignore` keeps the bundler from trying to
 * resolve the runtime-only specifier.
 */
export async function nativeDynamicImport(specifier) {
    return (await import(/* @vite-ignore */ specifier));
}
/**
 * Wrap a `fetch` with bounded cold-start retry. On a `cold_start_retry`
 * classification (502 / 504 / plain-503 / `x-keda-http-cold-start`) the wrapper
 * re-issues the request up to `maxRetries` times (default 4), waiting
 * `max(backoffMs(attempt), Retry-After)` between attempts and RE-classifying
 * each response — so a mid-sequence transition to 2xx returns immediately and a
 * transition to a terminal status (403/404/500/maintenance) is returned as-is.
 * A keyless mutation is never retried (mutation guard). On exhaustion the last
 * (still-cold) response is returned — the honest failure, not a throw.
 *
 * A `Retry-After` above 120 s counts as 120 s (`classifyResponse`). A caller `backoffMs` has no
 * ceiling, so the wait can pass 120 s. Each wait listens to the caller's
 * signal by the Fetch rule: `init.signal` when it is not `undefined` (`null` means none), else the
 * signal of a `Request` input. An abort ends the wait at once: the call rejects with the signal's
 * reason and sends nothing more. The default timer stops then too. An injected `sleep` runs on to
 * its own end, and its result goes nowhere (issue #3699).
 */
export function coldStartAwareFetch(baseFetch = fetch, opts = {}) {
    const maxRetries = opts.maxRetries ?? 4;
    const backoffMs = opts.backoffMs ?? coldStartBackoffMs;
    const sleep = opts.sleep;
    const wait = (ms, signal) => sleep === undefined ? startWait(ms, signal).done : sleepUnlessAborted(sleep, ms, signal);
    return async (input, init) => {
        let res = await baseFetch(input, init);
        let action = classifyResponse(res.status, res.headers.get('Retry-After'), res.headers.get('x-keda-http-cold-start'));
        if (action.type !== 'cold_start_retry')
            return res;
        // Mutation guard: a keyless non-idempotent request opts out of auto-retry.
        if (!isColdStartRetryable(input, init))
            return res;
        for (let attempt = 0; action.type === 'cold_start_retry'; attempt += 1) {
            if (attempt >= maxRetries)
                return res; // exhausted — surface the cold failure
            await wait(Math.max(backoffMs(attempt), action.retryAfterSec * 1000), callerSignalOf(input, init));
            res = await baseFetch(input, init);
            action = classifyResponse(res.status, res.headers.get('Retry-After'), res.headers.get('x-keda-http-cold-start'));
        }
        return res;
    };
}
/**
 * Cold-start-aware dynamic import of a remote ESM bundle. Wraps
 * `loadWithColdStartRetry` + `probeColdStart` + `bustBundleUrl`: a failed
 * `import()` triggers a probe of the bundle URL; a cold probe waits the backoff
 * and re-imports a cache-busted specifier (a browser caches a FAILED dynamic
 * import, so the distinct specifier forces a fresh fetch); a warm probe retries
 * immediately; a permanent probe rethrows. Returns the imported module
 * namespace.
 */
export function importRemoteBundle(url, opts = {}) {
    const fetchFn = opts.fetch ?? fetch;
    const sleep = opts.sleep ?? defaultSleep;
    const importModule = opts.importModule ?? (nativeDynamicImport);
    return loadWithColdStartRetry((attempt) => importModule(bustBundleUrl(url, attempt)), () => probeColdStart(url, fetchFn), sleep, { maxRetries: opts.maxRetries, backoffMs: opts.backoffMs });
}
/**
 * Stamp an `Idempotency-Key` on a request init so the cold-start transport will
 * auto-retry the mutation (and the server can dedup the replay). A fresh UUID
 * is generated when neither an existing header nor an explicit `key` is
 * present; an already-set `Idempotency-Key` is preserved (so the SAME value is
 * replayed across transport retries — the idempotency contract).
 */
export function withIdempotencyKey(init, key) {
    const headers = new Headers(init?.headers);
    if (!headers.has('Idempotency-Key')) {
        headers.set('Idempotency-Key', key ?? crypto.randomUUID());
    }
    return { ...init, headers };
}
//# sourceMappingURL=cold-start-fetch.js.map